import {
  FullScreenError,
  FullScreenSpinner,
} from '@insights-ltd/design-library/components';
import { useTranslation } from 'react-i18next';
import { Search, useSearch } from 'api/search';
import { useGetPaginatedOrganisations } from 'api/organisations/useGetPaginatedOrganisations';
import usePagination, {
  DEFAULT_CARD_ITEMS_PER_PAGE,
} from 'components/hooks/usePaginationV2';
import { useEffect } from 'react';
import { useNavigate } from 'react-router';
import TabLayout from './TabLayout';

const OrganisationsTab = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { searchString, setSearchString, searchTerm }: Search = useSearch();
  const {
    status,
    isFetching,
    data: { items = [], totalCount = 0 } = {},
    hasNextPage: hasMore,
    fetchNextPage: loadMore,
  } = useGetPaginatedOrganisations({ searchTerm });
  const paginator = usePagination(
    items,
    DEFAULT_CARD_ITEMS_PER_PAGE,
    totalCount,
  );
  const { pagedItems } = paginator;

  useEffect(() => {
    if (totalCount === 1 && !searchTerm) {
      navigate(`/organisations/${items[0].id}`, { replace: true });
    }
  }, [navigate, items, totalCount, searchTerm]);

  if (status === 'pending') {
    return <FullScreenSpinner message={t('ui.event-management.loading')} />;
  }
  if (status === 'error') {
    return (
      <FullScreenError
        message={t('ui.event-management.organisations.error-loading')}
      />
    );
  }

  const heading = searchTerm
    ? t(
        'ui.event-management.organisations.tab.subtitle.after.search.organisations',
      )
    : t(
        'ui.event-management.organisations.tab.subtitle.before.search.organisations',
      );

  const searchProps = {
    labelText: `${t(
      'ui.event-management.organisations.tab.search.box.label.prefix',
    )} ${t(
      'ui.event-management.organisations.tab.search.box.label.organisations',
    )}`,
    placeholder: t(
      'ui.event-management.organisations.tab.search.box.placeholder.text',
    ),
    inputState: searchString,
    handleTermChange: setSearchString,
  };
  const apiPaginationProps = {
    hasMoreToLoad: hasMore,
    isFetching,
    loadMore,
    loadingState: status,
  };
  return (
    <TabLayout
      heading={heading}
      searchProps={searchProps}
      paginator={paginator}
      apiPaginationProps={apiPaginationProps}
      organisationsOrGroups={pagedItems}
    />
  );
};

export default OrganisationsTab;
